import { Controller } from "@hotwired/stimulus"

/**
 * SizeComparisonController
 *
 * Provides a "compare against" dropdown on the product size add/edit form.
 * When a sibling size is selected from the dropdown, this controller injects
 * a small hint below every field that has a `data-comparison-field` attribute
 * on its surrounding `.col-*` container, showing whether the selected size
 * uses that field and what value it carries.
 *
 * The sibling sizes are embedded as JSON via the `sizesValue` Stimulus value,
 * which is set by the server on the `<form>` element.
 *
 * @example HTML structure expected per-field
 *   <div class="col-sm-6" data-comparison-field="expansion_max">
 *     <label>...</label>
 *     <input type="text" ...>
 *     <div class="form-text">...</div>
 *   </div>
 *
 * @example Usage in the form
 *   <form data-controller="size-comparison"
 *         data-size-comparison-sizes-value="[{...}, {...}]">
 *     <select data-action="change->size-comparison#selectChange">...</select>
 *     ...fields with data-comparison-field on their col containers...
 *   </form>
 */
export default class extends Controller {
  static values = {
    sizes: { type: Array, default: [] }
  }

  connect() {
    this.#injectHintContainers()
  }

  selectChange(event) {
    const rawId = event.target.value
    if (!rawId) {
      this.#hideAllHints()
      return
    }

    const sizeId = parseInt(rawId, 10)
    const size = this.sizesValue.find(s => s.id === sizeId)
    if (!size) return

    this.#updateAllHints(size)
  }

  // ============================================================
  // Private
  // ============================================================

  #injectHintContainers() {
    this.element.querySelectorAll("[data-comparison-field]").forEach(container => {
      const field = container.dataset.comparisonField
      if (container.querySelector(`.size-comparison-hint[data-field="${field}"]`)) return

      const hint = document.createElement("div")
      hint.className = "form-text size-comparison-hint d-none mt-1"
      hint.dataset.field = field
      container.appendChild(hint)
    })
  }

  #hideAllHints() {
    this.element.querySelectorAll(".size-comparison-hint").forEach(hint => {
      hint.classList.add("d-none")
    })
  }

  #updateAllHints(size) {
    this.element.querySelectorAll("[data-comparison-field]").forEach(container => {
      const field = container.dataset.comparisonField
      const hint = container.querySelector(`.size-comparison-hint[data-field="${field}"]`)
      if (!hint) return

      const value = size[field]
      const hasValue = value !== null && value !== undefined && value !== ""

      if (hasValue) {
        hint.innerHTML =
          `<span class="badge text-bg-info me-1"><i class="fa-solid fa-equals fa-xs" aria-hidden="true"></i></span> ` +
          this.#escapeHtml(String(value))
      } else {
        hint.innerHTML =
          `<span class="badge text-bg-secondary me-1"><i class="fa-solid fa-minus fa-xs" aria-hidden="true"></i></span> ` +
          `<em class="text-muted">Not used</em>`
      }

      hint.classList.remove("d-none")
    })
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
